export const AI_PANEL_COMMAND_EVENT = "convoke:aipanel-command";
export const AI_PANEL_GRID_REFRESH_EVENT = "convoke:aipanel-grid-refresh";

const PANEL_QUERY_KEYS = [
  "aiPanel",
  "aiPanelRoute",
  "aiPanelId",
  "aiPanelReferenceId",
  "aiPanelSessionId",
  "aiPanelParams"
];

const trimText = (value) => String(value ?? "").trim();

const getCommandValue = (commands) =>
  commands?.AIPanel ??
  commands?.aiPanel ??
  commands?.AI_PANEL ??
  null;

const parseMaybeJson = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed || !["{", "["].includes(trimmed[0])) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const normalizePanelKey = (routeOrKey) => {
  const value = trimText(routeOrKey).toLowerCase();
  if (!value) return "";
  if (value.includes("whsearch") || value.includes("warehouse-search") || value.includes("warehousesearch")) {
    return "whsearch";
  }
  if (value.includes("sosearch") || value.includes("source-object-search") || value.includes("sourceobjectsearch")) {
    return "sosearch";
  }
  if (value.includes("eosearch") || value.includes("entity-object-search") || value.includes("entityobjectsearch")) {
    return "eosearch";
  }
  if (value.includes("migration/manage") || value.includes("migration-project") || value.includes("migrationproject") || value.includes("migrationadvisor")) {
    return "migration";
  }
  if (value.includes("permissionset/manage") || value.includes("permission-set") || value.includes("permissionset")) {
    return "permissionset";
  }
  if (value.includes("edi/diagnostics") || value.includes("edi-diagnostics") || value.includes("edidiagnostics")) {
    return "edidiagnostics";
  }
  return value.replace(/^\/+|\/+$/g, "");
};

const parseCommandRouteAndValue = (commandValue, fallbackValue) => {
  const parsedCommand = parseMaybeJson(commandValue);
  if (parsedCommand && typeof parsedCommand === "object" && !Array.isArray(parsedCommand)) {
    return {
      route: parsedCommand.route ?? parsedCommand.Route ?? parsedCommand.screen ?? parsedCommand.Screen ?? parsedCommand.panel ?? parsedCommand.Panel,
      value: fallbackValue ?? parsedCommand.value ?? parsedCommand.Value ?? parsedCommand.params ?? parsedCommand.Params ?? parsedCommand
    };
  }

  const rawCommand = trimText(commandValue);
  if (!rawCommand) {
    return { route: "", value: fallbackValue };
  }

  if (fallbackValue !== undefined && fallbackValue !== null && trimText(fallbackValue) !== "") {
    return { route: rawCommand, value: fallbackValue };
  }

  const parts = rawCommand.split("|");
  if (parts.length > 1) {
    return {
      route: parts[0],
      value: parts.slice(1).join("|")
    };
  }

  return { route: rawCommand, value: fallbackValue };
};

const readFirst = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && trimText(value) !== "") {
      return trimText(value);
    }
  }
  return "";
};

const normalizeParamsObject = (source) => {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return {};
  }

  return Object.entries(source).reduce((result, [key, value]) => {
    const text = trimText(value);
    if (key && text) {
      result[key] = text;
    }
    return result;
  }, {});
};

const readParamsFromSearch = (search) => {
  const params = new URLSearchParams(search ?? "");
  const result = {};
  params.forEach((value, key) => {
    const text = trimText(value);
    if (key && text) {
      result[key] = text;
    }
  });
  return result;
};

const parseSerializedParams = (value) => {
  const parsed = parseMaybeJson(value);
  return normalizeParamsObject(parsed);
};

const parsePanelValue = (value) => {
  const parsed = parseMaybeJson(value);
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    const nestedParams = parsed.params ?? parsed.Params ?? parsed.queryParams ?? parsed.QueryParams;
    return {
      id: readFirst(parsed, ["id", "_id", "panelId", "PanelId", "migrationProjectId", "MigrationProjectId", "permissionSetId", "PermissionSetId", "wId", "wid", "warehouseId", "WarehouseId", "soId", "sourceObjectId", "SourceObjectId", "eoId", "entityObjectId", "EntityObjectId"]),
      referenceId: readFirst(parsed, ["referenceId", "referenceid", "panelReferenceId", "PanelReferenceId", "wdId", "wdid", "warehouseDeploymentId", "WarehouseDeploymentId"]),
      sessionId: readFirst(parsed, ["sessionId", "sessionid", "chatSessionId", "ChatSessionId", "sid"]),
      params: normalizeParamsObject(nestedParams ?? parsed)
    };
  }

  const rawValue = trimText(value);
  if (!rawValue) {
    return { id: "", referenceId: "", sessionId: "", params: {} };
  }

  const [idPart, queryPart = ""] = rawValue.split("|");
  const query = queryPart.replace(/^\?/, "");
  const params = new URLSearchParams(query);
  return {
    id: trimText(idPart),
    referenceId: trimText(params.get("referenceId") ?? params.get("wdId") ?? params.get("wdid") ?? params.get("warehouseDeploymentId")),
    sessionId: trimText(params.get("sessionId") ?? params.get("sid")),
    params: readParamsFromSearch(query)
  };
};

export function resolveAiPanelCommand(commands, params = {}) {
  const commandValue = getCommandValue(commands);
  if (commandValue === null || commandValue === undefined) {
    return null;
  }

  const { route, value } = parseCommandRouteAndValue(commandValue, params?.Value ?? params?.value);
  const panel = normalizePanelKey(route);
  if (!panel) {
    return null;
  }

  const parsedValue = parsePanelValue(value);
  return {
    panel,
    route: trimText(route),
    ...parsedValue
  };
}

export function buildAiPanelSearchParams(currentSearch, descriptor) {
  const nextParams = new URLSearchParams(currentSearch ?? "");
  for (const key of PANEL_QUERY_KEYS) {
    nextParams.delete(key);
  }

  if (!descriptor?.panel) {
    return nextParams;
  }

  nextParams.set("aiPanel", descriptor.panel);
  if (descriptor.route) nextParams.set("aiPanelRoute", descriptor.route);
  if (descriptor.id) nextParams.set("aiPanelId", descriptor.id);
  if (descriptor.referenceId) nextParams.set("aiPanelReferenceId", descriptor.referenceId);
  if (descriptor.sessionId) nextParams.set("aiPanelSessionId", descriptor.sessionId);
  const descriptorParams = normalizeParamsObject(descriptor.params);
  if (Object.keys(descriptorParams).length > 0) {
    nextParams.set("aiPanelParams", JSON.stringify(descriptorParams));
  }
  return nextParams;
}

export function clearAiPanelSearchParams(currentSearch) {
  const nextParams = new URLSearchParams(currentSearch ?? "");
  for (const key of PANEL_QUERY_KEYS) {
    nextParams.delete(key);
  }
  return nextParams;
}

export function readAiPanelDescriptorFromSearch(search) {
  const params = new URLSearchParams(search ?? "");
  const panel = trimText(params.get("aiPanel"));
  if (!panel) {
    return null;
  }

  return {
    panel,
    route: trimText(params.get("aiPanelRoute")),
    id: trimText(params.get("aiPanelId")),
    referenceId: trimText(params.get("aiPanelReferenceId")),
    sessionId: trimText(params.get("aiPanelSessionId")),
    params: parseSerializedParams(params.get("aiPanelParams"))
  };
}

const hasRequiredPanelContext = (descriptor) => {
  if (!descriptor?.panel) {
    return false;
  }

  if (descriptor.panel === "whsearch") {
    return !!descriptor.id && !!descriptor.referenceId;
  }

  if (descriptor.panel === "sosearch" || descriptor.panel === "eosearch" || descriptor.panel === "migration" || descriptor.panel === "permissionset" || descriptor.panel === "edidiagnostics") {
    return !!descriptor.id;
  }

  return true;
};

export function dispatchAiPanelCommand(commands, params = {}) {
  const descriptor = resolveAiPanelCommand(commands, params);
  if (!hasRequiredPanelContext(descriptor) || typeof window === "undefined") {
    return false;
  }

  window.dispatchEvent(new CustomEvent(AI_PANEL_COMMAND_EVENT, { detail: descriptor }));
  return true;
}
